import { graphql } from 'gatsby';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
// import '../translation/i18n';

import PageSEO from '../components/PageSEO';
import CharacteristicsSection from '../components/ui/CharacteristicsSection';
import Hero from '../components/ui/Hero';
import SanityLayout from '../components/ui/SanityLayout';
import AlternativeFundAssetCarouselModule, {
  AlternativeFundAsset,
} from '../components/ui/modules/AlternativeFundAssetCarouselModule';
import FormModule from '../components/ui/modules/FormModule';
import TextAndImageModule from '../components/ui/modules/TextAndImageModule';
import { ALL_LANGS } from '../constants';
import { SanityImageType } from '../graphql-fragments/SanityImage';
import { LocalizedSEO } from '../graphql-fragments/seo';
import { RawPortableText } from '../types/types';
import { withI18n } from '../utils/hocs';
import { localize } from '../utils/sanity';

export interface AlternativeFund {
  _id: string;
  __typename: 'SanityAlternativeFund';
  image: SanityImageType;
  videoUrl?: string;
  name: string;
  slug: {
    current: string;
  };
  isGoldenVisaElegible?: boolean;
  shortDescription: string;
  descriptionSection?: {
    title?: string;
    text?: RawPortableText;
    image: SanityImageType;
  };
  characteristics: Array<{
    title: string;
    additionalExplanation?: string;
    description: RawPortableText;
  }>;
  documents?: Array<{
    name: string;
    description: string;
    file?: {
      asset: {
        url: string;
        size: number;
      };
    };
    link?: string;
  }>;
  seo: LocalizedSEO;
}

interface LocalizedQueryData {
  sanityAlternativeFund: AlternativeFund;
  allSanityAlternativeFundAsset: {
    nodes: Array<AlternativeFundAsset>;
  };
  sanityAlternativeFundsSettings: {
    documentsSectionImage: SanityImageType;
  };
}

export const query = graphql`
  query GetAlternativeFundById($id: String!) {
    sanityAlternativeFund(_id: { eq: $id }) {
      ...AlternativeFund
    }
    allSanityAlternativeFundAsset(sort: { orderRank: ASC }) {
      nodes {
        ...AlternativeFundAsset
      }
    }
    sanityAlternativeFundsSettings {
      documentsSectionImage {
        ...SanityImage
      }
    }
  }
`;

const AlternativeFund = ({ data }: any): React.ReactElement => {
  const { i18n, t } = useTranslation();
  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: LocalizedQueryData = localize(data, langs);

  const {
    _id,
    image: fundImage,
    name,
    shortDescription,
    descriptionSection,
    videoUrl,
    characteristics,
    documents,
    seo,
  } = localizedData.sanityAlternativeFund;

  const heroRef = useRef<HTMLElement | null>(null);

  const assetsInvestedInThisFund = localizedData.allSanityAlternativeFundAsset.nodes.filter(
    asset => asset.fund._id === _id,
  );

  return (
    <SanityLayout heroRef={heroRef}>
      <PageSEO pageSEO={seo} defaultTitle={name} defaultDescription={shortDescription} />

      <Hero
        heroRef={heroRef}
        title={name}
        text={shortDescription}
        textSide="left"
        mobileBackgroundImage={fundImage}
        desktopBackgroundImage={fundImage}
        backgroundType="image"
        withSideVideo={!!videoUrl}
        videoUrl={videoUrl}
      />
      {descriptionSection && (
        <TextAndImageModule
          image={descriptionSection.image}
          title={descriptionSection.title}
          text={descriptionSection.text}
          backgroundColor="#E5F8E8"
          imageOrientation="landscape"
          imageSide="left"
        />
      )}
      <CharacteristicsSection
        title={t('fundCharacteristic.title', 'Características principais')}
        characteristics={characteristics}
      />
      {assetsInvestedInThisFund.length > 0 && (
        <AlternativeFundAssetCarouselModule
          assetsToUse="chooseManually"
          backgroundColor="#EFECFA"
          title={t('alternativeFund.assets_module_title', 'Ativos investidos neste Fundo')}
          assetsReferences={assetsInvestedInThisFund}
          noLink
        />
      )}
      {documents && documents.length > 0 && (
        <TextAndImageModule
          image={localizedData.sanityAlternativeFundsSettings.documentsSectionImage}
          title={t('alternativeFundDocuments.title', 'Documentos')}
          backgroundColor="#E5F8E8"
          imageOrientation="portrait"
          imageSide="right"
          fileRows={documents}
        />
      )}
      <FormModule />
    </SanityLayout>
  );
};

export default withI18n(AlternativeFund);
